/**
 * MZ-004 — multi-timezone pay clock.
 *
 * One worked minute can sit on three clocks at once: the provider (tenant)
 * clock, the work-location clock (facility, or the client's service location
 * for home care) and the worker's base clock. This module decides which clock
 * answers which payroll question. It does not price anything; the award
 * engines still price every minute.
 *
 * Accountable decisions (Paul, 5 Oct 2026), verified against primary award
 * text in docs/operations/clause-verifications/2026-10-05-mz004-multi-timezone-pay-semantics.md:
 *
 *   D1  Penalty windows (weekend, shift commencement/finish, spans of hours)
 *       read the WORK-LOCATION clock. Engines receive `workTimezone`.
 *   D2  An award public-holiday rate applies when the minute is a holiday at
 *       EITHER the work location OR the worker's base. Each calendar's date is
 *       read in its own zone. NES s 114 absence and payment follow the BASE.
 *   D3  One ordinary-hours averaging cycle per worker, keyed by BASE-timezone
 *       dates, separate from the tenant pay period. Rate-effective dates stay
 *       keyed to the pay period.
 *   D4  Durations are actual elapsed time (end − start in UTC). The engines
 *       already measure elapsed milliseconds; nothing here converts that.
 *   D5  DST start: elapsed time plus a separate ordinary top-up line so a
 *       permanent (full-time or part-time) worker's rostered ordinary pay is
 *       not reduced by the clock change. Casuals get elapsed time only.
 *   D6  The "day" for daily overtime triggers is the BASE-timezone calendar
 *       day.
 *
 * Single-zone invariant: when work zone = base zone = tenant zone, every
 * function here returns exactly what the pre-MZ-004 code computed with the
 * tenant timezone, so single-zone pay is byte-identical. The one deliberate
 * exception is D5, which only fires on a shift that crosses a DST-start gap.
 */

import { DEFAULT_TZ, dateOnlyInTz, dayBoundsInTz } from '../timezone';
import { roundMoney, sumMoney } from '../money-rounding';
import type { ShiftPayAllowanceLine, ShiftPayBreakdown } from '../schads';

/** Version of the clock-selection rules. Bump on any behaviour change. */
// 1.1.0 (6 Oct 2026): D5 applies only to DST-start transitions on or after
// DST_START_ORDINARY_TOP_UP_EFFECTIVE_FROM (Paul's scope decision).
// 1.2.1 (2026-10-08) — DST top-up amounts use exact half-up roundMoney.
export const MULTI_ZONE_PAY_CLOCK_VERSION = '1.2.1';

export const MZ004_CLAUSE_VERIFICATION_REF =
  'docs/operations/clause-verifications/2026-10-05-mz004-multi-timezone-pay-semantics.md';

/** Where a resolved zone came from. Recorded on multi-zone pay evidence. */
export type PayClockZoneSource =
  | 'FACILITY_TIMEZONE'
  | 'FACILITY_STATE'
  | 'CLIENT_STATE'
  | 'PRIMARY_FACILITY_TIMEZONE'
  | 'PRIMARY_FACILITY_STATE'
  | 'TENANT';

export interface PayClock {
  /** Provider clock. Keys pay periods and rate-effective dates only. */
  tenantTimezone: string;
  /** D1 — classifies penalty windows. */
  workTimezone: string;
  /** D2 (base calendar), D3 (cycle), D6 (daily triggers). */
  baseTimezone: string;
  /** Holiday state of the work location (null = unresolved). */
  workState: string | null;
  /** Holiday state of the worker's base (null = unresolved: base calendar not consulted). */
  baseState: string | null;
  /** Primary facility that anchors the base, when known (facility-scoped holidays). */
  baseFacilityId: string | null;
  workZoneSource: PayClockZoneSource;
  baseZoneSource: PayClockZoneSource;
}

export interface ResolvePayClockInput {
  tenantTimezone: string;
  /** Already-resolved work-location zone, or null to use the tenant clock. */
  workTimezone?: string | null;
  workZoneSource?: PayClockZoneSource | null;
  workState?: string | null;
  /** Already-resolved base zone, or null to use the tenant clock. */
  baseTimezone?: string | null;
  baseZoneSource?: PayClockZoneSource | null;
  baseState?: string | null;
  baseFacilityId?: string | null;
}

export class InvalidPayClockTimezoneError extends Error {
  readonly code = 'INVALID_PAY_CLOCK_TIMEZONE';

  constructor(field: string, value: string) {
    super(`${field} is not a valid IANA timezone: "${value}"`);
    this.name = 'InvalidPayClockTimezoneError';
  }
}

function isValidIanaTimezone(value: string): boolean {
  if (!value || !value.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-AU', { timeZone: value }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
}

function normState(state: string | null | undefined): string | null {
  const trimmed = typeof state === 'string' ? state.trim().toUpperCase() : '';
  return trimmed || null;
}

/**
 * Build a pay clock. Missing work or base zones fall back to the tenant clock,
 * which is exactly the pre-MZ-004 behaviour. An invalid zone string throws:
 * pricing a shift on a guessed clock is worse than refusing to price it.
 */
export function resolvePayClock(input: ResolvePayClockInput): PayClock {
  const tenantTimezone = input.tenantTimezone?.trim() ?? '';
  if (!isValidIanaTimezone(tenantTimezone)) {
    throw new InvalidPayClockTimezoneError('tenantTimezone', input.tenantTimezone);
  }
  const work = input.workTimezone?.trim() || null;
  const base = input.baseTimezone?.trim() || null;
  if (work && !isValidIanaTimezone(work)) {
    throw new InvalidPayClockTimezoneError('workTimezone', work);
  }
  if (base && !isValidIanaTimezone(base)) {
    throw new InvalidPayClockTimezoneError('baseTimezone', base);
  }
  return {
    tenantTimezone,
    workTimezone: work ?? tenantTimezone,
    baseTimezone: base ?? tenantTimezone,
    workState: normState(input.workState),
    baseState: normState(input.baseState),
    baseFacilityId: input.baseFacilityId ?? null,
    workZoneSource: work ? (input.workZoneSource ?? 'FACILITY_TIMEZONE') : 'TENANT',
    baseZoneSource: base ? (input.baseZoneSource ?? 'PRIMARY_FACILITY_TIMEZONE') : 'TENANT',
  };
}

/** The pre-MZ-004 clock: every question answered by the tenant zone. */
export function tenantOnlyPayClock(tenantTimezone: string, workState?: string | null): PayClock {
  return resolvePayClock({ tenantTimezone, workState: workState ?? null });
}

/** True when all three clocks are the same IANA zone. */
export function isSingleZonePayClock(clock: PayClock): boolean {
  return clock.workTimezone === clock.tenantTimezone && clock.baseTimezone === clock.tenantTimezone;
}

/** D1 — the zone an award engine must classify penalty windows in. */
export function payClockEngineTimezone(clock: PayClock): string {
  return clock.workTimezone;
}

export type PayClockHolidayCalendar = 'WORK' | 'BASE';

export interface PayClockPublicHolidayResult {
  isPublicHoliday: boolean;
  /** Calendars that matched, in WORK, BASE order. */
  calendars: PayClockHolidayCalendar[];
  workLocalDate: string;
  baseLocalDate: string;
}

/**
 * D2 — award public-holiday test for a shift.
 *
 * Attribution follows the existing engine contract: the shift's start instant
 * decides the holiday flag (the engines take one `isPublicHoliday` boolean per
 * priced span). Under D2 that instant is read in EACH calendar's own zone:
 *
 *   work holiday = isWorkHoliday(start read on the work clock)
 *   base holiday = isBaseHoliday(start read on the base clock)
 *
 * and the shift is a holiday shift if either matches. `isBaseHoliday` is only
 * consulted when the base state is known: an unresolved base must not widen
 * the holiday set (the work calendar alone is the pre-MZ-004 behaviour).
 *
 * Single-zone: work date = base date = tenant date. When the base state equals
 * the work state the result equals the pre-MZ-004 work-calendar answer.
 */
export function resolvePayClockPublicHoliday(args: {
  clock: PayClock;
  startTime: Date;
  isWorkHoliday: (localDate: string) => boolean;
  isBaseHoliday?: ((localDate: string) => boolean) | null;
}): PayClockPublicHolidayResult {
  const workLocalDate = dateOnlyInTz(args.startTime, args.clock.workTimezone);
  const baseLocalDate = dateOnlyInTz(args.startTime, args.clock.baseTimezone);
  const calendars: PayClockHolidayCalendar[] = [];
  if (args.isWorkHoliday(workLocalDate)) calendars.push('WORK');
  if (args.clock.baseState && args.isBaseHoliday && args.isBaseHoliday(baseLocalDate)) {
    calendars.push('BASE');
  }
  return {
    isPublicHoliday: calendars.length > 0,
    calendars,
    workLocalDate,
    baseLocalDate,
  };
}

/**
 * Prepare holiday spans at both calendars' local midnights. No pricing or
 * engine calls here: consumers must retain whole-shift loading and OT carry.
 * A start-only flag is insufficient for a crossing-midnight span.
 */
export function resolvePayClockPublicHolidaySegments(args: {
  clock: PayClock;
  startTime: Date;
  endTime: Date;
  isWorkHoliday: (localDate: string) => boolean;
  isBaseHoliday?: ((localDate: string) => boolean) | null;
}): Array<PayClockPublicHolidayResult & { startTime: Date; endTime: Date }> {
  const start = args.startTime.getTime(),
    end = args.endTime.getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start)
    throw new Error('Holiday span requires a valid start and later end');
  const spans: Array<PayClockPublicHolidayResult & { startTime: Date; endTime: Date }> = [];
  let cursor = start;
  while (cursor < end) {
    const instant = new Date(cursor);
    const nextMidnight = (timezone: string) =>
      Date.parse(dayBoundsInTz(dateOnlyInTz(instant, timezone), timezone).endUtc) + 1;
    const boundary = Math.min(
      end,
      nextMidnight(args.clock.workTimezone),
      nextMidnight(args.clock.baseTimezone),
    );
    if (boundary <= cursor) throw new Error('Holiday calendar boundary did not advance');
    spans.push({
      ...resolvePayClockPublicHoliday({ ...args, startTime: instant }),
      startTime: instant,
      endTime: new Date(boundary),
    });
    cursor = boundary;
  }
  return spans;
}

/**
 * D3 / D6 — the BASE-timezone business date of an instant.
 *
 * Callers that already hold the tenant-local business date for the event
 * (for example `timesheets.date`, derived in the tenant zone by the database)
 * pass it as `tenantBusinessDate`. When the base zone IS the tenant zone that
 * stored date is returned unchanged, so single-zone keys are byte-identical to
 * the pre-MZ-004 keys even where the stored date and the start instant differ.
 */
export function payClockBaseDate(args: {
  clock: PayClock;
  instant: Date | string;
  tenantBusinessDate?: string | null;
}): string {
  if (args.clock.baseTimezone === args.clock.tenantTimezone && args.tenantBusinessDate) {
    return args.tenantBusinessDate;
  }
  const instant = args.instant instanceof Date ? args.instant : new Date(args.instant);
  return dateOnlyInTz(instant, args.clock.baseTimezone);
}

/** ISO Monday (YYYY-MM-DD) of a YYYY-MM-DD calendar date. Calendar arithmetic only. */
export function isoMondayOfLocalDate(localDate: string): string {
  const d = new Date(`${localDate}T00:00:00Z`);
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d.getTime() + diff * 86_400_000);
  return `${monday.getUTCFullYear()}-${String(monday.getUTCMonth() + 1).padStart(2, '0')}-${String(
    monday.getUTCDate(),
  ).padStart(2, '0')}`;
}

/* ─────────────────────────── D5: DST start top-up ─────────────────────────── */

/** Local wall-clock reading of an instant, expressed as a UTC-epoch millisecond count. */
function wallClockMs(instant: Date, timezone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const hour = get('hour') === 24 ? 0 : get('hour');
  return (
    Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second')) +
    instant.getUTCMilliseconds()
  );
}

/**
 * Hours of wall-clock time skipped by a forward (DST-start) transition inside
 * [startTime, endTime) on the given clock. Zero when no forward transition is
 * crossed, including at DST end (the clock repeats time; elapsed is longer and
 * already paid under D4). Sydney/Adelaide/Hobart/Melbourne skip 1 hour; Lord
 * Howe skips 30 minutes; Perth/Brisbane/Darwin have no DST.
 */
export function dstStartSkippedHours(args: {
  startTime: Date;
  endTime: Date;
  timezone: string;
}): number {
  const elapsedMs = args.endTime.getTime() - args.startTime.getTime();
  if (!(elapsedMs > 0)) return 0;
  const wallMs =
    wallClockMs(args.endTime, args.timezone) - wallClockMs(args.startTime, args.timezone);
  const skippedMs = wallMs - elapsedMs;
  if (skippedMs <= 0) return 0;
  return Math.round((skippedMs / 3_600_000) * 10_000) / 10_000;
}

/**
 * D5 effective-from (Paul, 6 Oct 2026: "from the release date only").
 *
 * The DST-start ordinary top-up applies only to a forward transition whose
 * work-local date is ON OR AFTER this date. It therefore never applies to the
 * 4 October 2026 transition, on recompute or on snapshot, and periods already
 * finalised or approved keep their stored engine output. Back-pay for past
 * DST-start nights is a separate decision for the payroll owner.
 *
 * No Australian DST-start transition falls between this date and 3 October
 * 2027, so any release before then is equivalent to "from the release date".
 * Decision and evidence: MZ004_CLAUSE_VERIFICATION_REF (D5, F5) and
 * docs/operations/mz004-multi-zone-pay-design-2026-10-05.md.
 */
export const DST_START_ORDINARY_TOP_UP_EFFECTIVE_FROM = '2026-10-05';

/**
 * Forward (DST-start) transitions inside [startTime, endTime) on a clock, as
 * the work-local date after the change and the wall-clock time skipped.
 * Scans in 15-minute steps; Australian transitions happen on the hour.
 */
export function dstStartTransitions(args: {
  startTime: Date;
  endTime: Date;
  timezone: string;
}): Array<{ localDate: string; skippedHours: number }> {
  const startMs = args.startTime.getTime();
  const endMs = args.endTime.getTime();
  if (!(endMs > startMs)) return [];
  const offsetAt = (ms: number) => wallClockMs(new Date(ms), args.timezone) - ms;
  const result: Array<{ localDate: string; skippedHours: number }> = [];
  const step = 15 * 60 * 1000;
  let previousOffset = offsetAt(startMs);
  for (let ms = Math.min(startMs + step, endMs); ; ms = Math.min(ms + step, endMs)) {
    const offset = offsetAt(ms);
    if (offset > previousOffset) {
      result.push({
        localDate: dateOnlyInTz(new Date(ms), args.timezone),
        skippedHours: Math.round(((offset - previousOffset) / 3_600_000) * 10_000) / 10_000,
      });
    }
    previousOffset = offset;
    if (ms >= endMs) break;
  }
  return result;
}

export const DST_START_ORDINARY_TOP_UP_CODE = 'DST_START_ORDINARY_TOP_UP_ALLOWANCE';
export const DST_START_ORDINARY_TOP_UP_COMPONENT_CODE = 'DST_START_ORDINARY_TOP_UP';

const ORDINARY_CATEGORY_BY_AWARD: Record<string, string> = {
  MA000100: 'SCHADS_ORDINARY',
  MA000034: 'NURSES_ORDINARY',
  MA000018: 'AGED_CARE_ORDINARY',
};

interface DstTopUpInput {
  startTime: Date;
  endTime: Date;
  timezone?: string;
  employmentType: string;
  segmentKind?: string;
}

function round2(value: number): number {
  return roundMoney(value);
}

/**
 * D5 — append the DST-start ordinary top-up line to an engine breakdown.
 *
 * Applies only to permanent (full-time or part-time) WORK segments that
 * cross a forward transition on the work clock (D1 — rosters are written in
 * the work location's wall time). Paid at the ordinary hourly rate for the
 * skipped wall-clock time, capped at the segment's ordinary (non-overtime)
 * hours. Elapsed-time pay (D4) is untouched; this is a separate line.
 *
 * Returns the breakdown unchanged (same object) whenever the rule does not
 * fire, so every shift that does not cross a DST start is byte-identical.
 */
export function applyDstStartOrdinaryTopUp(
  breakdown: ShiftPayBreakdown,
  input: DstTopUpInput,
  awardCode: string,
): ShiftPayBreakdown {
  if (input.employmentType === 'casual') return breakdown;
  if ((input.segmentKind ?? 'work') !== 'work') return breakdown;
  if (breakdown.cancelledButPaid === false) return breakdown;
  if (!(breakdown.baseHourlyRate > 0) || !(breakdown.hoursWorked > 0)) return breakdown;

  const skipped = dstStartSkippedHours({
    startTime: input.startTime,
    endTime: input.endTime,
    // Same default clock the engines classify with when no zone is supplied.
    timezone: input.timezone ?? DEFAULT_TZ,
  });
  if (skipped <= 0) return breakdown;
  // Effective-from guard: only transitions on or after the release date.
  const effectiveSkipped = dstStartTransitions({
    startTime: input.startTime,
    endTime: input.endTime,
    timezone: input.timezone ?? DEFAULT_TZ,
  })
    .filter((transition) => transition.localDate >= DST_START_ORDINARY_TOP_UP_EFFECTIVE_FROM)
    .reduce((sum, transition) => sum + transition.skippedHours, 0);
  if (effectiveSkipped <= 0) return breakdown;
  const ordinaryHours = Math.max(0, breakdown.hoursWorked - (breakdown.overtimeHours ?? 0));
  const hours = Math.round(Math.min(skipped, effectiveSkipped, ordinaryHours) * 10_000) / 10_000;
  if (hours <= 0) return breakdown;

  // eslint-disable-next-line statura/no-floating-point-money -- hours are 4-dp rounded and the engine's decimal ordinary rate is multiplied once, then cent-rounded, exactly as the automatic minimum-engagement top-up does.
  const amount = round2(hours * breakdown.baseHourlyRate);
  const minutes = Math.round(hours * 60);
  const line: ShiftPayAllowanceLine = {
    code: DST_START_ORDINARY_TOP_UP_CODE,
    clauseRef: `MZ-004 decision D5 (${MZ004_CLAUSE_VERIFICATION_REF})`,
    description: `Daylight saving start: ${minutes} minutes of rostered ordinary time lost to the clock change, paid at the ordinary rate`,
    amount,
    units: hours,
    unitType: 'hours',
    componentCode: DST_START_ORDINARY_TOP_UP_COMPONENT_CODE,
    ...(ORDINARY_CATEGORY_BY_AWARD[awardCode]
      ? { xeroEarningsCategory: ORDINARY_CATEGORY_BY_AWARD[awardCode] }
      : {}),
    taxable: true,
    superable: true,
  };
  return {
    ...breakdown,
    // eslint-disable-next-line statura/no-floating-point-money -- both operands are cent-rounded payroll component amounts; round2 re-normalises.
    allowancesPay: round2(breakdown.allowancesPay + amount),
    allowanceLines: [...breakdown.allowanceLines, line],

    grossTotal: sumMoney(breakdown.grossTotal, amount),
  };
}
